import { FormikErrors } from "formik";

/**
 * Helper function to get first error string from complex FormikErrors object
 * @param errors - FormikErrors object or string
 * @returns First error message string found
 */
export const getFirstErrorMessage = (
  errors: FormikErrors<unknown> | string,
): string => {
  if (typeof errors === "string") return errors;
  if (!errors) return "";

  // For nested objects like address_attributes
  const flattenedErrors: string[] = [];

  // Recursively collect all error strings
  const collectErrors = (
    obj: FormikErrors<unknown> | string | undefined,
  ): void => {
    if (typeof obj === "string" && obj) {
      flattenedErrors.push(obj);
    } else if (obj && typeof obj === "object") {
      Object.values(obj).forEach((value) =>
        collectErrors(value as FormikErrors<unknown> | string | undefined),
      );
    }
  };

  collectErrors(errors);
  return flattenedErrors[0] || "";
};

/**
 * Normalizes signup source fields to ensure both are set or both are null.
 * This prevents partial signup source data from being submitted, which would cause API errors.
 *
 * @param signupSourceId - The signup source ID (may be "none", empty, or a valid ID)
 * @param signupSourceType - The signup source type (may be empty or a valid type)
 * @returns An object with both fields set to valid values or both set to null
 *
 * @example
 * // Valid signup source
 * normalizeSignupSource("user-123", "User")
 * // Returns: { signup_source_id: "user-123", signup_source_type: "User" }
 *
 * @example
 * // Invalid or incomplete signup source
 * normalizeSignupSource("", "User")
 * // Returns: { signup_source_id: null, signup_source_type: null }
 */
export const normalizeSignupSource = (
  signupSourceId: string | undefined | null,
  signupSourceType: string | undefined | null,
): {
  signup_source_id: string | null;
  signup_source_type: string | null;
} => {
  const hasValidSignupSource =
    signupSourceId &&
    signupSourceId !== "none" &&
    signupSourceType &&
    signupSourceType !== "";

  return {
    signup_source_id: hasValidSignupSource ? signupSourceId : null,
    signup_source_type: hasValidSignupSource ? signupSourceType : null,
  };
};
